import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { catsApi, feedingRequestsApi } from '../utils/api';
import { handleApiError, formatDate } from '../utils/helpers';

const CatDetailsPage = () => {
  const { catId } = useParams();
  const navigate = useNavigate();
  const [cat, setCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [requestSent, setRequestSent] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const defaultImage = '/cat-placeholder.png';
  
  // Determine if the user is a feeder or regular user
  const userType = localStorage.getItem('userType');
  const isFeeder = userType === 'feeder';

  // Fetch cat details
  useEffect(() => {
    const fetchCat = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const catData = await catsApi.getCatById(catId);
        setCat(catData);
        console.log('Cat details:', catData);
        
        // Set up photos array based on the response structure
        let photosArray = [];
        
        // First, check if catPhotos array of objects is present
        if (catData.catPhotos && catData.catPhotos.length > 0) {
          // Extract paths from the catPhotos objects
          photosArray = catData.catPhotos.map(photo => photo.path);
          console.log('Using catPhotos object array, extracted paths:', photosArray);
        }
        // Next, check for catPhotoPaths array of strings
        else if (catData.catPhotoPaths && catData.catPhotoPaths.length > 0) {
          photosArray = catData.catPhotoPaths;
          console.log('Using catPhotoPaths array of URLs:', photosArray);
        }
        // Then, fall back to mainPhoto if available
        else if (catData.mainPhoto) {
          photosArray = [catData.mainPhoto];
          console.log('Using mainPhoto as fallback:', catData.mainPhoto);
        }
        // Finally, use the default placeholder
        else {
          photosArray = [defaultImage];
          console.log('No photos found, using default placeholder');
        }
        
        setPhotos(photosArray);
      } catch (error) {
        console.error('Error fetching cat details:', error);
        setError(handleApiError(error));
      } finally {
        setLoading(false);
      }
    };
    
    if (catId) {
      fetchCat();
    }
  }, [catId, defaultImage]);

  // Navigate to previous photo
  const prevPhoto = () => {
    if (photos.length <= 1) return;
    setCurrentPhotoIndex((prevIndex) => 
      (prevIndex - 1 + photos.length) % photos.length
    );
  };
  
  // Navigate to next photo
  const nextPhoto = () => {
    if (photos.length <= 1) return;
    setCurrentPhotoIndex((prevIndex) => 
      (prevIndex + 1) % photos.length
    );
  };

  // Handle feed button click for feeders
  const handleFeedClick = () => {
    navigate(`/feeder/create-feeding/${catId}`);
  };
  
  // Handle edit button click for feeders
  const handleEditClick = () => {
    navigate(`/feeder/edit-cat/${catId}`);
  };

  // Handle feed request click for regular users
  const handleFeedRequest = async () => {
    if (requestLoading || requestSent) return;
    
    try {
      setRequestLoading(true);
      await feedingRequestsApi.createFeedingRequest(catId);
      setRequestSent(true);
    } catch (error) {
      console.error('Error sending feed request:', error);
      setError(handleApiError(error));
    } finally {
      setRequestLoading(false);
    }
  };
  
  // Get formatted date
  const getFormattedDate = (date) => {
    if (!date) return 'Never';
    return formatDate(date, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="cat-details-page">
      <div className="page-header">
        <h1 className="page-title">Cat Details</h1>
        <div className="header-actions">
          <button
            className="button back-button"
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
        </div>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Error state */}
      {error && !loading && (
        <div className="error-message">
          <p>{error}</p>
          <button className="button" onClick={() => navigate('/feeder/my-cats')}>
            Go Back to My Cats
          </button>
        </div>
      )}
      
      {/* Cat details */}
      {!loading && !error && cat && (
        <div className="cat-detail-card">
          <div className="cat-detail-header">
            <div className="cat-detail-info">
              <h2 className="cat-name">{cat.name}</h2>
              <div className="cat-gender-badge">
                {cat.gender === 'male' ? 'Male ♂️' : 'Female ♀️'}
              </div>
            </div>
            
            <div className="cat-detail-actions">
              {isFeeder ? (
                // Feeder actions
                <>
                  <button
                    className="button"
                    onClick={handleFeedClick}
                  >
                    Feed Now
                  </button>
                  
                  <button
                    className="button secondary"
                    onClick={handleEditClick}
                  >
                    Edit Cat
                  </button>
                </>
              ) : (
                // Regular user actions
                <>
                  <button
                    className={`button ${requestSent ? 'success' : ''}`}
                    onClick={handleFeedRequest}
                    disabled={requestLoading || requestSent}
                  >
                    {requestLoading ? 'Requesting...' : requestSent ? 'Requested ✓' : 'Request Feeding'}
                  </button>
                  
                  <button
                    className="button secondary"
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </button>
                </>
              )}
            </div>
          </div>
          
          <div className="cat-detail-content">
            <div className="cat-photos-carousel">
              <div className="main-photo">
                <img 
                  src={photos[currentPhotoIndex]} 
                  alt={cat.name}
                  onError={(e) => {
                    console.log('Image failed to load, using fallback');
                    e.target.src = defaultImage;
                  }}
                />
                
                {photos.length > 1 && (
                  <>
                    <button
                      className="photo-nav-button prev-button"
                      onClick={prevPhoto}
                      aria-label="Previous photo"
                    >
                      ‹
                    </button>
                    <button
                      className="photo-nav-button next-button"
                      onClick={nextPhoto}
                      aria-label="Next photo"
                    >
                      ›
                    </button>
                  </>
                )}
              </div>
              
              {photos.length > 1 && (
                <div className="photo-thumbnails">
                  {photos.map((photo, index) => (
                    <div 
                      key={index}
                      className={`photo-thumbnail ${index === currentPhotoIndex ? 'active' : ''}`}
                      onClick={() => setCurrentPhotoIndex(index)}
                    >
                      <img 
                        src={photo} 
                        alt={`${cat.name} - photo ${index + 1}`}
                        onError={(e) => {
                          e.target.src = defaultImage;
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="cat-detail-info-section">
              <div className="info-group">
                <h3>Basic Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Age:</span>
                    <span className="info-value">{cat.age ? `${cat.age} months` : 'Unknown'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Last Fed:</span>
                    <span className="info-value">{getFormattedDate(cat.lastFed)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Pending Requests:</span>
                    <span className="info-value">{cat.pendingRequests || cat.nonProcessedFeedingRequestsCount || 0}</span>
                  </div>
                </div>
              </div>
              
              <div className="info-group">
                <h3>Description</h3>
                <p className="cat-description">{cat.description || 'No description available.'}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatDetailsPage; 